/**
 * Exercise Card Component
 *
 * Displays an exercise question with answer input (multiple choice or free text)
 * Words in the question are clickable to explore their meaning
 */

import React, { useState, useEffect } from 'react';
import { FaFlag } from 'react-icons/fa';
import { Exercise } from '../../types/grammarPractice';
import ClickableText from '../common/ClickableText';

interface ExerciseCardProps {
  exercise: Exercise;
  onSubmit: (answer: string) => void;
  disabled: boolean;
  onOpenDiscussion: () => void;
}

const ExerciseCard: React.FC<ExerciseCardProps> = ({ exercise, onSubmit, disabled, onOpenDiscussion }) => {
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const isMultipleChoice = exercise.options && exercise.options.length > 0;

  // Reset selected answer when exercise changes
  useEffect(() => {
    setSelectedAnswer('');
  }, [exercise.exercise_id]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate answer is not empty
    if (!selectedAnswer || selectedAnswer.trim() === '') {
      console.warn('ExerciseCard: Attempted to submit empty answer');
      return;
    }
    
    onSubmit(selectedAnswer.trim());
  };

  const handleOptionSelect = (option: string) => {
    if (!disabled) {
      setSelectedAnswer(option);
    }
  };

  return (
    <div className="card bg-base-200 shadow-xl relative">
      <div className="card-body">
        {/* Flag icon for discussion - top right */}
        <div className="absolute top-4 right-4">
          <button
            onClick={onOpenDiscussion}
            className="btn btn-ghost btn-circle btn-sm"
            aria-label="Open discussion"
            title="Discuss this exercise"
          >
            <FaFlag className="text-base-content/60 hover:text-base-content transition-colors" />
          </button>
        </div>

        <p className="text-sm text-base-content/60 mb-2">{exercise.grammar_topic_name}</p>

        <div className="mb-6">
          <ClickableText
            text={exercise.question}
            className="text-2xl font-semibold"
            asParagraph
          />
        </div>

        <form onSubmit={handleSubmit}>
          {isMultipleChoice ? (
            <div className="space-y-2 mb-4">
              {exercise.options!.map((option, index) => (
                <div
                  key={index}
                  className={`card bg-base-100 cursor-pointer hover:bg-base-300 transition-colors ${
                    selectedAnswer === option ? 'ring-2 ring-primary' : ''
                  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  onClick={() => handleOptionSelect(option)}
                >
                  <div className="card-body py-3 px-4">
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="answer"
                        className="radio radio-primary"
                        checked={selectedAnswer === option}
                        onChange={() => handleOptionSelect(option)}
                        disabled={disabled}
                      />
                      <ClickableText text={option} className="text-base" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="form-control mb-4">
              <input
                type="text"
                placeholder="Type your answer here..."
                className="input input-bordered input-lg"
                value={selectedAnswer}
                onChange={(e) => setSelectedAnswer(e.target.value)}
                disabled={disabled}
                autoFocus
              />
            </div>
          )}

          <div className="card-actions justify-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={disabled || !selectedAnswer.trim()}
            >
              Check Answer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExerciseCard;
